import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  COUNTRY_CODES,
  getDisplayFlagIso,
  normalizeCode,
  sanitizePhoneInput,
} from '../../utils/bridgeStaff';
import CountryFlag from './CountryFlag';

const optionKey = (option) => option.selectValue || option.value;

export default function CountryCodeSelect({
  value,
  bridgeCountry,
  companyCountry,
  onChange,
  person,
  disabled = false,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const code = normalizeCode(value);
  const selectedKey = person?.phoneCountryIso === 'ca' && code === '1' ? 'ca' : code;
  const canadaSelected = selectedKey === 'ca';
  const flagIso = getDisplayFlagIso(
    code,
    bridgeCountry,
    companyCountry,
    canadaSelected ? 'ca' : undefined,
  );

  const handleChange = (selected) => {
    const option = COUNTRY_CODES.find((c) => optionKey(c) === selected);
    if (!option) return;
    onChange({
      ...person,
      countryCode: option.value,
      phoneCountryIso: option.iso === 'ca' ? 'ca' : undefined,
      phone: sanitizePhoneInput(person.phone, option.value),
    });
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuHeight = COUNTRY_CODES.length * 28 + 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow < menuHeight && rect.top > menuHeight
        ? rect.top - menuHeight - 2
        : rect.bottom + 2;
      setMenuStyle({
        top,
        left: rect.left,
        minWidth: Math.max(rect.width, 112),
      });
    };
    place();
    const close = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (event.target?.closest?.('.country-code-menu')) return;
      setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const menu = open && menuStyle
    ? createPortal(
      <ul className="country-code-menu" role="listbox" style={menuStyle}>
        {COUNTRY_CODES.map((option) => {
          const key = optionKey(option);
          const selected = key === selectedKey;
          return (
            <li key={key}>
              <button
                type="button"
                role="option"
                aria-selected={selected}
                aria-label={`${option.label} +${option.value}`}
                className={selected ? 'is-selected' : undefined}
                onClick={() => handleChange(key)}
              >
                <CountryFlag iso={option.iso} />
                <span>+{option.value}</span>
              </button>
            </li>
          );
        })}
      </ul>,
      document.body,
    )
    : null;

  return (
    <div
      ref={rootRef}
      className={`country-code-select${disabled ? ' is-disabled' : ''}${open ? ' is-open' : ''}`}
    >
      <button
        type="button"
        className="country-code-select-trigger"
        disabled={disabled}
        aria-label="Country calling code"
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Country calling code"
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
      >
        <CountryFlag iso={flagIso} title={canadaSelected ? 'Canada' : (bridgeCountry || 'Country')} />
        <span className="country-dial-code">+{code}</span>
        <span className="country-code-caret" aria-hidden="true" />
      </button>
      {menu}
    </div>
  );
}
